import React, { useState, useRef, useEffect } from "react"
import { Check, ChevronRight, Circle } from "lucide-react"

const DropdownMenu = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  return (
    <div ref={dropdownRef} className="relative inline-block">
      {React.Children.map(children, child =>
        React.cloneElement(child, { isOpen, setIsOpen })
      )}
    </div>
  )
}

const DropdownMenuTrigger = React.forwardRef(({ className = "", children, isOpen, setIsOpen, asChild = false, ...props }, ref) => {
  const Comp = asChild ? "div" : "button"
  
  return (
    <Comp
      ref={ref}
      className={className}
      onClick={() => setIsOpen(!isOpen)}
      {...props}
    >
      {children}
    </Comp>
  )
})

const DropdownMenuContent = React.forwardRef(({ 
  className = "", 
  align = "end", 
  sideOffset = 4, 
  children, 
  isOpen, 
  ...props 
}, ref) => {
  if (!isOpen) return null

  const alignClass = align === "end" ? "right-0" : "left-0"
  
  return (
    <div
      ref={ref}
      className={`absolute z-50 min-w-[8rem] overflow-hidden rounded-md border bg-white p-1 text-gray-900 shadow-md ${alignClass} mt-2 ${className}`}
      style={{ top: `calc(100% + ${sideOffset}px)` }}
      {...props}
    >
      {children}
    </div>
  )
})

const DropdownMenuItem = React.forwardRef(({ className = "", inset = false, ...props }, ref) => (
  <div
    ref={ref}
    className={`relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 focus:bg-gray-100 ${inset ? 'pl-8' : ''} ${className}`}
    {...props}
  />
))

const DropdownMenuSeparator = React.forwardRef(({ className = "", ...props }, ref) => (
  <div
    ref={ref}
    className={`-mx-1 my-1 h-px bg-gray-200 ${className}`}
    {...props}
  />
))

const DropdownMenuLabel = React.forwardRef(({ className = "", inset = false, ...props }, ref) => (
  <div
    ref={ref}
    className={`px-2 py-1.5 text-sm font-semibold ${inset ? 'pl-8' : ''} ${className}`}
    {...props}
  />
))

// Simplified versions of other components
const DropdownMenuGroup = ({ children }) => <div>{children}</div>
const DropdownMenuPortal = ({ children }) => <>{children}</>
const DropdownMenuSub = ({ children }) => <div>{children}</div>
const DropdownMenuSubContent = DropdownMenuContent
const DropdownMenuSubTrigger = DropdownMenuItem
const DropdownMenuRadioGroup = ({ children }) => <div>{children}</div>
const DropdownMenuCheckboxItem = DropdownMenuItem
const DropdownMenuRadioItem = DropdownMenuItem
const DropdownMenuShortcut = ({ className = "", ...props }) => (
  <span className={`ml-auto text-xs tracking-widest opacity-60 ${className}`} {...props} />
)

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
}